import { Controller } from "@hotwired/stimulus"

// Collects a card with Stripe's Payment Element, confirms a SetupIntent, then
// submits the form with the intent id so the server can start the trial. No
// subscription exists until the card has been verified.
export default class extends Controller {
  static targets = ["form", "card", "error", "submit", "setupIntent", "applePayNote", "terms", "fallback"]
  static values = { publishableKey: String, setupIntentUrl: String, returnUrl: String }

  // Stripe.js is a plain script in the page head while this controller arrives
  // in a deferred module, so which lands first isn't guaranteed. Wait for it
  // rather than assume, and load it here if the page never carried it.
  static STRIPE_JS_URL = "https://js.stripe.com/acacia/stripe.js"
  // How long to give the head tag before loading our own copy with listeners,
  // and how long to wait overall on a slow connection before giving up.
  static RETRY_AFTER_MS = 3000
  static DEADLINE_MS = 30000
  // When the script never arrives, ask three hosts directly so the failure
  // report says whether js.stripe.com alone is unreachable (a dead connection
  // the browser keeps reusing), all of Stripe is, or the visitor is offline.
  static PROBE_TIMEOUT_MS = 5000
  static PROBE_URLS = {
    stripe_js: "https://js.stripe.com/acacia/stripe.js",
    stripe_checkout: "https://checkout.stripe.com/",
    same_origin: "/robots.txt",
  }

  static FAILURE_MESSAGES = {
    blocked:
      "Your browser blocked our payment provider, Stripe (js.stripe.com). Turn off content or ad blockers for this page, then reload.",
    did_not_run:
      "Our payment provider, Stripe, downloaded but couldn't start in this browser. Check for privacy settings or extensions that block site data, or try another browser, then reload.",
    storage_blocked:
      "Your browser is blocking site data (cookies and storage) for this site, which our payment provider, Stripe, needs. Allow it for atlasobscura.com, then reload.",
    timeout:
      "Our payment provider, Stripe, is taking too long to load. Try a private or incognito window, or clear your browser's cached data, then reload this page.",
  }

  async connect() {
    this.submitting = false
    this.confirmed = false
    this.submitTarget.disabled = true
    this.cardTarget.textContent = "Loading payment form…"

    const failure = await this.loadStripeJs()
    if (failure) {
      this.cardTarget.textContent = ""
      const messageKey = failure.reason === "did_not_run" && !failure.storageOk ? "storage_blocked" : failure.reason
      this.errorTarget.textContent = this.constructor.FAILURE_MESSAGES[messageKey]
      // The in-page form can't be used now; swap its button and terms for
      // Stripe's hosted page, which lives on another host and so can work
      // even when the script from js.stripe.com never does.
      // The `hidden` attribute loses to the button's own display class, so
      // toggle Tailwind's class instead.
      this.cardTarget.classList.add("hidden")
      this.submitTarget.classList.add("hidden")
      this.termsTarget.classList.add("hidden")
      this.fallbackTarget.hidden = false
      this.reportLoadFailure(failure)
      return
    }

    this.cardTarget.textContent = ""
    this.submitTarget.disabled = false
    this.stripe = Stripe(this.publishableKeyValue)
    this.elements = this.stripe.elements({
      mode: "setup",
      currency: "usd",
      paymentMethodTypes: ["card"],
    })
    this.paymentElement = this.elements.create("payment")
    this.paymentElement.mount(this.cardTarget)
    // The $1 verification note only applies to Apple Pay, so show it only
    // while that is the selected payment method.
    this.paymentElement.on("change", (event) => {
      this.applePayNoteTarget.hidden = event.value?.type !== "apple_pay"
    })
  }

  disconnect() {
    this.paymentElement?.destroy()
  }

  // Resolves to null once window.Stripe exists, or to {reason, elapsedMs}.
  // The head tag carries no listeners and may already have failed before this
  // runs, so after a short grace period a second tag is added purely to learn
  // whether the browser refuses the script (its error event fires at once),
  // fetches it but it never defines Stripe (load fires, global stays undefined:
  // a non-JS response, a startup exception such as blocked site data, or a
  // browser too old for the bundle), or is merely slow (keep waiting).
  loadStripeJs() {
    if (window.Stripe) return Promise.resolve(null)

    const startedAt = Date.now()
    const headTagPresent = !!document.querySelector('script[src^="https://js.stripe.com/"]')

    // A script that downloads but throws while starting up (Firefox with site
    // data blocked does this) surfaces only as a window error event. Keep the
    // first one from Stripe's origin so the failure report says what broke.
    let scriptError = null
    const onError = (event) => {
      if (scriptError || !/js\.stripe\.com/.test(event.filename || "")) return
      scriptError = `${event.message} (${event.filename.replace(/^https?:\/\//, "").slice(0, 60)}:${event.lineno})`
    }
    window.addEventListener("error", onError)

    return new Promise((resolve) => {
      let settled = false
      const finish = (failure) => {
        if (settled) return
        settled = true
        window.removeEventListener("error", onError)
        resolve(failure && { ...failure, scriptError, storageOk: this.storageAvailable() })
      }

      const addTag = () => {
        if (window.Stripe) return
        const script = document.createElement("script")
        // OneTrust's autoblock decides when src is assigned, so this must come first.
        script.setAttribute("data-ot-ignore", "")
        // A different URL, so the browser fetches afresh rather than reusing
        // whatever happened to the head tag's request. Stripe's CDN serves the
        // bundle unchanged with a query string.
        script.src = `${this.constructor.STRIPE_JS_URL}?retry=${Date.now()}`
        script.addEventListener("error", () => finish({ reason: "blocked", elapsedMs: Date.now() - startedAt, headTagPresent }))
        script.addEventListener("load", () => {
          // Give the script's own top-level code a tick to define the global.
          setTimeout(() => {
            if (!window.Stripe) finish({ reason: "did_not_run", elapsedMs: Date.now() - startedAt, headTagPresent })
          }, 0)
        })
        document.head.appendChild(script)
      }

      // No head tag at all (e.g. a layout without :head_tags): load it now.
      // Otherwise give the head tag a moment before adding the probe copy.
      setTimeout(addTag, headTagPresent ? this.constructor.RETRY_AFTER_MS : 0)

      const check = () => {
        if (settled) return
        if (window.Stripe) return finish(null)
        if (Date.now() - startedAt > this.constructor.DEADLINE_MS) {
          return finish({ reason: "timeout", elapsedMs: Date.now() - startedAt, headTagPresent })
        }
        setTimeout(check, 50)
      }
      check()
    })
  }

  stripeRequestTimings() {
    try {
      return performance
        .getEntriesByType("resource")
        .filter((entry) => entry.name.startsWith("https://js.stripe.com/"))
        .map((entry) => ({
          duration_ms: Math.round(entry.duration),
          transfer_bytes: entry.transferSize,
          status: entry.responseStatus,
        }))
    } catch (e) {
      return null
    }
  }

  // Stripe.js touches localStorage as it starts; a browser that blocks site
  // data makes that throw, and the script never defines its global.
  storageAvailable() {
    try {
      window.localStorage.length
      return true
    } catch (e) {
      return false
    }
  }

  // Resolves to {result, ms}: "ok" (any response, even opaque), "error"
  // (refused at once: DNS failure, reset, blocked) or "hang" (nothing within
  // the probe timeout). A cache-busting query keeps the browser from
  // answering from its own store.
  async probe(url) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), this.constructor.PROBE_TIMEOUT_MS)
    const startedAt = Date.now()
    const separator = url.includes("?") ? "&" : "?"
    try {
      await fetch(`${url}${separator}probe=${startedAt}`, { mode: "no-cors", cache: "no-store", signal: controller.signal })
      return { result: "ok", ms: Date.now() - startedAt }
    } catch (e) {
      return { result: e.name === "AbortError" ? "hang" : "error", ms: Date.now() - startedAt }
    } finally {
      clearTimeout(timer)
    }
  }

  async probeHosts() {
    const entries = Object.entries(this.constructor.PROBE_URLS)
    const results = await Promise.all(entries.map(([, url]) => this.probe(url)))
    return Object.fromEntries(entries.map(([name], i) => [name, results[i]]))
  }

  async reportLoadFailure(failure) {
    if (typeof window.posthog === "undefined") return

    // Only a timeout leaves the cause open; the other reasons already say
    // what happened, and the probes would just delay the report.
    const probes = failure.reason === "timeout" ? await this.probeHosts() : null

    window.posthog.capture("contributor_payment_form_failed", {
      reason: failure.reason,
      elapsed_ms: failure.elapsedMs,
      head_tag_present: failure.headTagPresent,
      script_error: failure.scriptError,
      storage_ok: failure.storageOk,
      cookies_enabled: navigator.cookieEnabled,
      // One entry per request for the script: did the head tag's request
      // complete at all, and did any bytes come back?
      stripe_requests: this.stripeRequestTimings(),
      online: navigator.onLine,
      connection_type: navigator.connection?.effectiveType,
      visibility_state: document.visibilityState,
      // A full buffer would explain a missing stripe_requests entry.
      resource_entries: performance.getEntriesByType("resource").length,
      probe_stripe_js: probes?.stripe_js?.result,
      probe_stripe_js_ms: probes?.stripe_js?.ms,
      probe_stripe_checkout: probes?.stripe_checkout?.result,
      probe_stripe_checkout_ms: probes?.stripe_checkout?.ms,
      probe_same_origin: probes?.same_origin?.result,
      probe_same_origin_ms: probes?.same_origin?.ms,
    })
  }

  async submit(event) {
    // The programmatic requestSubmit() below re-enters this same handler via
    // the submit->contributor-payment#submit action. Let that resubmit pass
    // through untouched so the browser actually posts the confirmed intent.
    if (this.confirmed) return

    event.preventDefault()
    if (this.submitting) return
    this.submitting = true
    this.submitTarget.disabled = true
    this.errorTarget.textContent = ""

    const validationResult = await this.validateElements()
    if (validationResult === null) return
    if (validationResult.error) return this.fail(validationResult.error.message)

    const clientSecret = await this.createSetupIntent()
    if (!clientSecret) return

    const confirmResult = await this.confirmSetup(clientSecret)
    if (confirmResult === null) return
    if (confirmResult.error) return this.fail(confirmResult.error.message)

    this.setupIntentTarget.value = confirmResult.setupIntent.id
    this.confirmed = true
    this.formTarget.requestSubmit()
  }

  async validateElements() {
    try {
      return await this.elements.submit()
    } catch (e) {
      this.fail("We couldn't start your trial. Please try again.")
      return null
    }
  }

  async confirmSetup(clientSecret) {
    try {
      return await this.stripe.confirmSetup({
        elements: this.elements,
        clientSecret: clientSecret,
        confirmParams: { return_url: this.returnUrlValue },
        redirect: "if_required",
      })
    } catch (e) {
      this.fail("We couldn't start your trial. Please try again.")
      return null
    }
  }

  async createSetupIntent() {
    try {
      // Read the CSRF token at submit time: Aon refreshes it client-side after
      // the page loads.
      const response = await fetch(this.setupIntentUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      })
      const data = await response.json()
      if (!response.ok) {
        this.fail(data.error || "We couldn't start your trial. Please try again.")
        return null
      }
      return data.client_secret
    } catch (e) {
      this.fail("We couldn't start your trial. Please try again.")
      return null
    }
  }

  fail(message) {
    this.errorTarget.textContent = message
    this.submitTarget.disabled = false
    this.submitting = false
  }
}
